import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Customer, ContractLog } from "@/types/models";
import { supabase } from "@/integrations/supabase/client";
import { sendWhatsappMessage, uploadContractPdf } from "@/integrations/supabase/services";
import { generateContractPdf } from "@/lib/contractGenerator";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileText, Send, Loader2, ExternalLink } from "lucide-react";

interface CustomerDetailsModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
}

const CustomerDetailsModal = ({ customer, isOpen, onClose }: CustomerDetailsModalProps) => {
  const [logs, setLogs] = useState<ContractLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (isOpen && customer) {
      const fetchLogs = async () => {
        setIsLoadingLogs(true);
        try {
          const { data, error } = await supabase
            .from('contract_logs')
            .select('*')
            .eq('customer_id', customer.id)
            .order('sent_at', { ascending: false });
          if (error) throw error;
          setLogs(data || []);
        } catch (e) {
          console.error("Error al obtener la bitácora de contratos:", e);
        } finally {
          setIsLoadingLogs(false);
        }
      };
      fetchLogs();
    }
  }, [isOpen, customer]);

  const handleResend = async () => {
    if (!customer) return;
    setIsSending(true);
    try {
      let contractUrl = customer.contract_url;
      
      if (!contractUrl) {
        if (!customer.id_photo_url) {
          toast.error("El cliente no tiene una foto de cédula registrada. Agréguela editando el cliente.");
          return;
        }
        
        toast.info("Generando contrato personalizado...");
        const blob = await generateContractPdf({
          name: customer.name,
          idCard: customer.id_card,
          phone: customer.phone,
          idPhotoUrl: customer.id_photo_url
        });
        
        const contractFileName = `contracts/contract-${customer.id_card}-${Date.now()}.pdf`;
        contractUrl = await uploadContractPdf(blob, contractFileName);
        
        // Actualizar cliente en base de datos
        const { error: updateError } = await supabase
          .from('customers')
          .update({ contract_url: contractUrl, contract_sent: true })
          .eq('id', customer.id);
        
        if (updateError) throw updateError;
        
        // Actualizar la referencia local para evitar volver a generarlo
        customer.contract_url = contractUrl;
        customer.contract_sent = true;
      }
      
      toast.info("Enviando contrato por WhatsApp...");
      await sendWhatsappMessage({
        cardID: customer.id_card,
        template: "welcome",
        variables: [customer.name],
        contract: true,
        contractUrl: contractUrl || undefined
      });
      
      toast.success("Contrato enviado exitosamente por WhatsApp");
      
      // Actualizar bitácora en la interfaz
      const { data } = await supabase
        .from('contract_logs')
        .select('*')
        .eq('customer_id', customer.id)
        .order('sent_at', { ascending: false });
      setLogs(data || []);
    } catch (e: any) {
      console.error("Error al enviar el contrato:", e);
      toast.error("Error al enviar el contrato: " + e.message);
    } finally {
      setIsSending(false);
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Detalles del Cliente</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-4">
          <div className="text-center">
            <p className="text-left text-sm font-medium text-gray-600">Foto de cédula</p>
            {customer.id_photo_url ? (
              <div className="mb-4">
                <img
                  src={customer.id_photo_url}
                  alt="Foto de cédula"
                  className="w-full max-w-sm mx-auto rounded-lg border shadow-sm"
                />
              </div>
            ) : (
              <div className="mb-4 w-full max-w-sm mx-auto h-32 bg-gray-100 rounded-lg border flex items-center justify-center">
                <span className="text-gray-500">Sin foto</span>
              </div>
            )}
          </div>

          <div className="text-center">
            <p className="text-left text-sm font-medium text-gray-600">Foto de perfil</p>
            {customer.profile_photo_url ? (
              <div className="mb-2">
                <img
                  src={customer.profile_photo_url}
                  alt="Foto de perfil"
                  className="w-full max-w-sm mx-auto rounded-lg border shadow-sm"
                />
              </div>
            ) : (
              <div className="mb-4 w-full max-w-sm mx-auto h-32 bg-gray-100 rounded-lg border flex items-center justify-center">
                <span className="text-gray-500">Sin foto</span>
              </div>
            )}
          </div>
          
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-2 border-b pb-2">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Nombre:</label>
                <p className="text-base font-semibold text-gray-800">{customer.name}</p>
              </div>
              
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Cédula:</label>
                <p className="text-base text-gray-800">{customer.id_card}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 border-b pb-2">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Teléfono:</label>
                <p className="text-base text-gray-800">{customer.phone}</p>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Registrado:</label>
                <p className="text-sm text-gray-800">{new Date(customer.created_at).toLocaleString()}</p>
              </div>
            </div>

            <div className="border-b pb-3">
              <label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Contrato Legal:</label>
              {customer.contract_url ? (
                <div className="flex items-center gap-2">
                  <a
                    href={customer.contract_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded bg-emerald-100 hover:bg-emerald-250 text-emerald-850 text-xs font-bold transition-colors"
                  >
                    <FileText size={14} />
                    Ver Contrato Activo (PDF)
                  </a>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No se ha generado un contrato personalizado aún.</p>
              )}
            </div>

            {/* Acción de Envío por WhatsApp */}
            <div className="pt-2">
              <Button
                type="button"
                onClick={handleResend}
                disabled={isSending}
                className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 rounded-md shadow-sm transition-colors"
              >
                {isSending ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Procesando envío...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {customer.contract_url ? 'Reenviar Contrato por WhatsApp' : 'Generar y Enviar Contrato'}
                  </>
                )}
              </Button>
            </div>

            {/* Bitácora de Envíos */}
            <div className="pt-4 border-t">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Bitácora de Envíos (Historial)</h3>
              {isLoadingLogs ? (
                <div className="flex items-center justify-center py-4 space-x-2 text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin text-primary" />
                  <span>Cargando historial...</span>
                </div>
              ) : logs.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-2">No hay registros de envíos de contratos para este cliente.</p>
              ) : (
                <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1">
                  {logs.map((log) => (
                    <div key={log.id} className="flex items-center justify-between p-2 bg-slate-50 rounded border text-xs">
                      <div>
                        <p className="font-semibold text-slate-700">{new Date(log.sent_at).toLocaleString()}</p>
                        <p className="text-slate-500 flex items-center gap-1">
                          Estado: <span className="text-emerald-600 font-semibold">{log.status === 'sent' ? 'Enviado' : log.status}</span>
                        </p>
                      </div>
                      <a
                        href={log.contract_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-700 transition-colors"
                        title="Ver copia de este envío"
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerDetailsModal;
